import { lighten } from '@mentimeter/ragnar-colors';
import { BRAND_STROKE_WIDTH } from '@mentimeter/global-variables';
import type { SegmentT } from '@mentimeter/http-clients';
import { d3, wordWrap, shouldUpdateText } from './d3';
import { NORMAL_FONT_SIZE, LARGE_FONT_SIZE } from './utils';

const LEGEND_PADDING = 25;
const TITLE_HEIGHT = 70;
const TITLE_MARGIN = 25;
const TITLE_TOTAL_HEIGHT = TITLE_HEIGHT + TITLE_MARGIN;
const ITEM_HEIGHT = 35;
const ITEM_MARGIN = 5;
const ITEM_TOTAL_HEIGHT = ITEM_HEIGHT + ITEM_MARGIN;
const CIRCLE_RADIUS = 10;
const TRANSITION_DURATION = 1000;

interface T {
  selector: string;
  colors: string[];
  segments: SegmentT[];
  title: string;
  textColor: string;
  width: number;
}

/**
 * SegmentationLegend: key for multiple choice results with segmentation on another question's results
 */
export class SegmentationLegend implements T {
  readonly selector: string;
  readonly colors: string[];
  readonly segments: SegmentT[];
  readonly title: string;
  readonly textColor: string;
  readonly width: number;
  height: number = 0;
  textWidth: number = 0;

  /**
   * @param selector: string, selection of a dom node, e.g., #segmentation-${questionId}
   * @param colors: array of color strings for circles, e.g., ['green', 'yellow', 'blue']
   * @param segments: array of segments
   * @param title: string, name of the question being segmented on
   * @param textColor: string, color for title
   * @param width: number, width of the legend in pixels
   */
  constructor(props: T) {
    this.selector = props.selector;
    this.colors = props.colors;
    this.segments = props.segments;
    this.title = props.title;
    this.textColor = props.textColor;
    this.width = props.width;
  }

  /**
   * Getter for the height of the legend based on constants and the number of segments
   *
   * @returns number, height in pixels
   */
  public getHeight = (): number => {
    return (
      LEGEND_PADDING +
      TITLE_TOTAL_HEIGHT +
      this.segments.length * ITEM_TOTAL_HEIGHT
    );
  };

  /**
   * Binds text data to the legend title and appends the title elements
   */
  private updateTitle = (): void => {
    d3.select(this.selector)
      .selectAll('.legend-title')
      .data([this.title])
      .join('text')
      .attr('class', 'legend-title')
      .attr('fill', this.textColor)
      .attr('font-weight', 600)
      .attr('dy', '1em')
      .text((d) => d)
      .call(wordWrap, {
        maxWidth: this.textWidth,
        maxHeight: TITLE_HEIGHT,
        fontSize: LARGE_FONT_SIZE,
      });
  };

  private addHoverEffect = (): void => {
    d3.select(this.selector)
      .selectAll('.legend-item')
      .on('mouseover', (_event, d: any) => {
        const segmentIndex = d.position;
        d3.selectAll(`.bar-segment:not(.bar-segment-${segmentIndex})`).attr(
          'opacity',
          0.2,
        );
      })
      .on('mouseout', (_event, d: any) => {
        const segmentIndex = d.position;
        d3.selectAll(`.bar-segment:not(.bar-segment-${segmentIndex})`).attr(
          'opacity',
          1,
        );
      });
  };

  /**
   * Binds data to the legend items and appends a circle and label to represent each
   *
   * @returns resolved animation promise
   */
  private updateItems = (): Promise<void> => {
    // Select
    const legendItems = d3
      .select(this.selector)
      .selectAll<SVGGElement, SegmentT>('.legend-item')
      .data(this.segments);
    // Enter
    const legendItemsEnter = legendItems
      .enter()
      .append('g')
      .attr('class', (_, i) => `legend-item legend-item-${i}`)
      .attr('opacity', 0)
      .attr('stroke-opacity', 0)
      .attr('transform', `translate(${CIRCLE_RADIUS}, ${TITLE_TOTAL_HEIGHT})`);
    const legendItemsContainer = legendItemsEnter
      .append('rect')
      .attr('fill', 'transparent')
      .attr('width', this.width)
      .attr('height', ITEM_TOTAL_HEIGHT)
      .attr('x', -CIRCLE_RADIUS)
      .attr('y', -ITEM_TOTAL_HEIGHT / 2)
      .attr('stroke', 'green');
    const legendItemsCircle = legendItemsEnter
      .append('circle')
      .attr('r', CIRCLE_RADIUS)
      .attr('fill', (_, i) => this.colors[i % this.colors.length] ?? 'black')
      .attr('stroke-width', BRAND_STROKE_WIDTH);
    const legendItemsText = legendItemsEnter
      .append('text')
      .attr('fill', this.textColor)
      .attr('x', CIRCLE_RADIUS * 2)
      .attr('dy', '0.35em');
    // Update
    const legendItemsTransition = legendItems
      .merge(legendItemsEnter)
      .transition()
      .duration(TRANSITION_DURATION)
      .ease(d3.easeElastic)
      .attr('opacity', 1)
      .attr(
        'transform',
        (_, i) =>
          `translate(${CIRCLE_RADIUS}, ${
            TITLE_TOTAL_HEIGHT + i * ITEM_TOTAL_HEIGHT
          })`,
      )
      .end();
    legendItems
      .select<SVGCircleElement>('circle')
      .merge(legendItemsCircle)
      .attr('stroke', (_, i) =>
        lighten(this.colors[i % this.colors.length] ?? 'black', 0.2),
      )
      .attr('fill', (_, i) => this.colors[i % this.colors.length] ?? 'black');
    const itemsSelection = legendItems
      .select<SVGTextElement>('text')
      .merge(legendItemsText);
    legendItems.select<SVGRectElement>('rect').merge(legendItemsContainer);
    itemsSelection.attr('fill', this.textColor);
    if (
      shouldUpdateText({
        selection: itemsSelection,
        texts: itemsSelection.data().map((s) => s.label),
        maxWidth: this.textWidth,
        maxHeight: ITEM_HEIGHT,
      })
    ) {
      itemsSelection
        .text((d) => d.label)
        .call(wordWrap, {
          maxWidth: this.textWidth,
          maxHeight: ITEM_HEIGHT,
          fontSize: NORMAL_FONT_SIZE,
        });
    }
    // Exit
    legendItems.exit().remove();

    return legendItemsTransition;
  };

  /**
   * Visualization updater
   *
   * @remarks
   * Must resolve animations before other chained commands can be executed, like taking a screenshot with PhantomJS
   *
   * @returns resolved animation promise
   */
  public update = (): Promise<void> => {
    d3.select(this.selector).attr(
      'transform',
      `translate(${LEGEND_PADDING}, ${LEGEND_PADDING})`,
    );
    this.textWidth = this.width - 4 * CIRCLE_RADIUS;

    this.updateTitle();
    this.addHoverEffect();
    const itemsTransition = this.updateItems();

    return itemsTransition;
  };
}
